import { useMemo, useState } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { emptyTrash, listTrash, purgeTrash, restoreTrash } from '../api/trash.ts';
import { useAuth } from '../store/auth.ts';
import { formatBytes } from '../lib/format.ts';
import { toast } from '../store/toast.ts';
import { EmptyState } from '../components/common/EmptyState.tsx';
import { ConfirmDialog } from '../components/common/ConfirmDialog.tsx';
import { TrashRow } from '../components/trash/TrashRow.tsx';
import { IconRestore, IconSearch, IconTrash } from '../lib/icons.tsx';

const COLUMNS = '32px 1fr 1fr 110px 120px';

export function TrashPage() {
  const ns = useAuth((s) => s.namespaces[0]);
  const queryClient = useQueryClient();
  const [confirm, setConfirm] = useState<'empty' | 'purge' | null>(null);
  const [checked, setChecked] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  /**
   * 목록을 쪽 단위로 불러온다.
   *
   * 예전에는 한 번에 200개만 받고 끝이어서, 그보다 많이 지운 사람은 나머지를
   * 볼 방법이 없었다(서버는 커서를 주고 있었는데 화면이 쓰지 않았다).
   */
  const query = useInfiniteQuery({
    queryKey: ['trash', ns?.id],
    queryFn: ({ pageParam }) => listTrash(ns!.id, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: Boolean(ns),
  });

  const pages = query.data?.pages ?? [];
  const all = useMemo(() => pages.flatMap((p) => p.items), [pages]);
  const totalSize = pages[0]?.total_size ?? 0;
  const retentionDays = pages[0]?.retention_days ?? 30;

  const items = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q === '') return all;
    return all.filter((i) => i.name.toLowerCase().includes(q)
      || i.original_path.toLowerCase().includes(q));
  }, [all, search]);

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['trash', ns?.id] });
    void queryClient.invalidateQueries({ queryKey: ['nodes', ns?.id] });
    void queryClient.invalidateQueries({ queryKey: ['usage'] });
    setChecked([]);
    setExpanded(null);
  };

  const restore = useMutation({
    mutationFn: (ids: string[]) => restoreTrash(ns!.id, ids),
    onSuccess: (result) => {
      const renamed = result.restored.filter((r) => r.renamed).length;
      // 건너뛴 항목을 알리지 않으면 사용자는 복원된 줄 알고 넘어간다
      if (result.skipped.length > 0) {
        toast.error(new Error(
          `${result.restored.length}건 복원, ${result.skipped.length}건 실패`
          + ` (${result.skipped[0]?.reason ?? '원인 불명'})`,
        ));
      } else {
        toast.success(renamed > 0
          ? `복원했습니다 (이름이 겹쳐 ${renamed}건은 이름을 바꿨습니다)`
          : '복원했습니다');
      }
      refresh();
    },
    onError: (e: Error) => { toast.error(e); },
  });

  const purge = useMutation({
    mutationFn: (ids: string[]) => purgeTrash(ns!.id, ids),
    onSuccess: (result) => {
      toast.success(`${result.deleted_items}개 항목을 영구 삭제했습니다`);
      setConfirm(null); refresh();
    },
    onError: (e: Error) => { toast.error(e); setConfirm(null); },
  });

  const emptyAll = useMutation({
    mutationFn: () => emptyTrash(ns!.id),
    onSuccess: (result) => {
      toast.success(`휴지통을 비웠습니다 (${formatBytes(result.freed_bytes)} 확보)`);
      setConfirm(null); refresh();
    },
    onError: (e: Error) => { toast.error(e); setConfirm(null); },
  });

  if (!ns) return null;

  const shownIds = items.map((i) => i.id);
  const allChecked = shownIds.length > 0 && shownIds.every((id) => checked.includes(id));

  return (
    <div className="page">
      <div className="page__head">
        <h1 className="page__title">휴지통</h1>
        <span className="muted">
          {all.length}개 · {formatBytes(totalSize)}
        </span>
        <div className="spacer" />

        {all.length > 0 && (
          <div className="search search--sm">
            <IconSearch size={15} className="faint" />
            <input
              className="search__input" placeholder="이름·위치 검색"
              value={search} onChange={(e) => { setSearch(e.target.value); }}
            />
          </div>
        )}

        {checked.length > 0 && (
          <>
            <button className="btn" onClick={() => { restore.mutate(checked); }}>
              <IconRestore size={16} /> 복원 ({checked.length})
            </button>
            <button className="btn btn--danger" onClick={() => { setConfirm('purge'); }}>
              영구 삭제
            </button>
          </>
        )}
        <button
          className="btn" disabled={all.length === 0}
          onClick={() => { setConfirm('empty'); }}
        >
          <IconTrash size={16} /> 휴지통 비우기
        </button>
      </div>

      <div className="page__body">
        {query.isLoading && <div className="empty"><span className="spinner" /></div>}

        {!query.isLoading && all.length === 0 && (
          <EmptyState
            icon={<IconTrash size={40} className="faint" />}
            title="휴지통이 비어 있습니다"
            description={`삭제한 항목은 ${retentionDays}일 동안 보관된 뒤 자동으로 지워집니다.`}
          />
        )}

        {all.length > 0 && items.length === 0 && (
          <EmptyState title="검색 결과가 없습니다" description={`'${search}' 와 맞는 항목이 없습니다.`} />
        )}

        {items.length > 0 && (
          <div className="table">
            <div className="table__head" style={{ gridTemplateColumns: COLUMNS }}>
              <input
                className="checkbox" type="checkbox" checked={allChecked}
                aria-label="모두 선택"
                onChange={(e) => {
                  // 검색 중이면 보이는 것만 다룬다. 안 보이는 항목이 함께
                  // 선택되면 사용자가 의도하지 않은 것을 지우게 된다.
                  setChecked(e.target.checked
                    ? Array.from(new Set([...checked, ...shownIds]))
                    : checked.filter((id) => !shownIds.includes(id)));
                }}
              />
              <span>이름</span><span>원래 위치</span><span>크기</span><span>남은 기간</span>
            </div>

            {items.map((item) => (
              <TrashRow
                key={item.id}
                item={item}
                namespaceId={ns.id}
                columns={COLUMNS}
                checked={checked.includes(item.id)}
                onToggle={(on) => {
                  setChecked(on
                    ? [...checked, item.id]
                    : checked.filter((id) => id !== item.id));
                }}
                expanded={expanded === item.id}
                onExpand={() => { setExpanded(expanded === item.id ? null : item.id); }}
              />
            ))}
          </div>
        )}

        {query.hasNextPage && search.trim() === '' && (
          <div className="row" style={{ justifyContent: 'center', padding: 'var(--sp-4)' }}>
            <button
              className="btn"
              disabled={query.isFetchingNextPage}
              onClick={() => { void query.fetchNextPage(); }}
            >
              {query.isFetchingNextPage ? <span className="spinner" /> : '더 보기'}
            </button>
          </div>
        )}
      </div>

      {confirm === 'empty' && (
        <ConfirmDialog
          title="휴지통 비우기"
          message={`${all.length}개 항목(${formatBytes(totalSize)})을 영구히 삭제합니다.\n`
            + '이 작업은 되돌릴 수 없습니다.'}
          confirmLabel="영구 삭제" danger busy={emptyAll.isPending}
          onConfirm={() => { emptyAll.mutate(); }}
          onCancel={() => { setConfirm(null); }}
        />
      )}
      {confirm === 'purge' && (
        <ConfirmDialog
          title="영구 삭제"
          message={`선택한 ${checked.length}개 항목을 영구히 삭제합니다.\n`
            + '이 작업은 되돌릴 수 없습니다.'}
          confirmLabel="영구 삭제" danger busy={purge.isPending}
          onConfirm={() => { purge.mutate(checked); }}
          onCancel={() => { setConfirm(null); }}
        />
      )}
    </div>
  );
}
